import React, {useState, useEffect, useMemo}		from 'react';
import {useNavigate, useParams, useSearchParams}	from 'react-router-dom';
import moment										from 'moment';
import {useReactTable, ColumnDef, getCoreRowModel,
	getFilteredRowModel, getSortedRowModel, getFacetedRowModel,
	getFacetedUniqueValues, getFacetedMinMaxValues} from '@tanstack/react-table';

import DevicesBadge									from '../ressources/components/Badge/DevicesBadges';
import Button										from '../ressources/components/Button';
import Icon											from '../ressources/components/Icon';
import Load											from '../ressources/components/Load/Load';
import Badge										from '../ressources/components/Badge';
import Tabs											from '../ressources/components/Tabs';
import {OrderCard}									from '../ressources/components/Order';
import ModalFilter									from '../ressources/components/ModalFilter';
import Section										from '../ressources/components/Section';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import {BadgeColor}									from '../ressources/components/Badge/interface';
import {DeviceCard}									from '../ressources/components/Device';
import Modal										from '../ressources/components/Modal';
import Input										from '../ressources/components/Input';
import DocumentItem									from '../ressources/components/Document';
import Avatar										from '../ressources/components/Avatar';
import Table										from '../ressources/components/Table';
import DevicesBadges								from '../ressources/components/Badge/DevicesBadges';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {getOrderLimitedApi, getOrdersApi,
	putOrderAssignApi}								from '../redux/order/order';
import {closeUpload, pushUpload, setUploadPercent}	from '../redux/documents/documents';
import {TabsValues}									from '../ressources/components/Tabs/interface';
import {Funder}										from '../redux/contracts/interface';
import {Order, OrderAddressList, OrderUseData}		from '../redux/order/interface';
import {User}										from '../redux/user/interface';
import {FilterChecked}								from '../ressources/components/Modal/interface';
import {SelectValues}								from '../ressources/components/Select/interface';
import {InputValues}								from '../ressources/components/Input/interface';
import {LanguageType}								from '../ressources/language/type';

import useWindowSize								from '../ressources/functions/useWindowSize';
import {ft_formatFirstUpperCase, ft_formatPrice,
	ft_formatUserName}								from '../ressources/functions/format';
import {ft_genNotification, ft_pushNotification}	from '../ressources/functions/notification';
import ft_cssVar									from '../ressources/functions/getCssVar';
import {ft_checkOrderFilters, ft_sortOrderByDate}	from '../ressources/functions/filters';

import img_filter									from '../ressources/images/icon/filter.svg';
import img_truck									from '../ressources/images/icon/truck.svg';
import img_boxColored								from '../ressources/images/icon/box-colored.svg';
import img_calendar									from '../ressources/images/icon/calendar-date.svg';
import img_file										from '../ressources/images/icon/file.svg';
import img_download									from '../ressources/images/icon/download.svg';
import img_deviceIcon								from '../ressources/images/icon/devices/computer.svg';
import img_ticket									from '../ressources/images/icon/ticket.svg';
import img_flipBackward								from '../ressources/images/icon/flip-backward.svg';
import img_arrowDown								from '../ressources/images/icon/arrow/down.svg';
import img_search									from '../ressources/images/icon/search.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import './Order.css';

interface Props {
	limited?: boolean
}

function OrderView(props: Props) {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const orders = useAppSelector(state => state.order.data);
	const address = useAppSelector(state => state.address.data);
	const orderLoad = useAppSelector(state => state.order.isLoad);
	const contracts = useAppSelector(state => state.contracts.data);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const lang = useAppSelector(state => state.settings.lang);
	const documentsUpload = useAppSelector(state => state.documents.uploadDocument);
	const notificationId = useAppSelector(state => state.notifications.lastId);

	const [editData, set_editData] = useState<Order | undefined>(undefined);
	const [indexAddress, set_indexAddress] = useState(0);
	const [menu, set_menu] = useState<TabsValues>('trackOrder');
	const [uploadModal, set_uploadModal] = useState(false);
	const [documents, set_documents] = useState<Record<string, File | undefined>>({});
	const [uploadLoad, set_uploadLoad] = useState(false);
	const [firstname, set_firstname] = useState<InputValues>('');
	const [lastname, set_lastname] = useState<InputValues>('');
	const [firstnameSignatory, set_firstnameSignatory] = useState<InputValues>('');
	const [lastnameSignatory, set_lastnameSignatory] = useState<InputValues>('');
	const [documentDate, set_documentDate] = useState<InputValues>('');
	const [modalUpload, set_modalUpload] = useState<null | string>(null);

	const [filters, set_filters] = useState<FilterChecked[]>([]);
	const [filterModal, set_filterModal] = useState(false);
	const [sort, set_sort] = useState<SelectValues>('');
	const [search, set_search] = useState<InputValues>('');

	const [force, set_force] = useState(0);

	const [init, set_init] = useState(false);

	const navigate = useNavigate();
	const params = useParams();
	const sizes = useWindowSize();
	const isComputer = sizes.width > 767;
	const [searchParams] = useSearchParams();
	const TEXTE = LANGUAGE(lang);

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	const useData = useMemo(() => {
		const result: OrderUseData[] = [];

		orders.forEach((order) => {
			order.addressList.forEach((addressList, i) => {
				if (!ft_checkOrderFilters(filters, addressList))
					return;

				result.push({
					...addressList,
					...order.data,
					i,
					_id:     order._id,
					price:   order.price,
					number:  order.number,
					iLength: order.addressList.length
				});
			});
		});

		return result;
	}, [orders, filters]);

	const columns = useMemo<ColumnDef<OrderUseData>[]>(() => [
		{
			id:         'number',
			header:     TEXTE.order.number,
			accessorFn: row => `${row?.number}${row?.iLength > 1 ? `-${row?.i + 1}` : ''}`
		},
		{
			id:         'date',
			header:     TEXTE.global.date,
			accessorFn: row => row.creationDate,
			cell:       v => moment(v.row.original.creationDate).format('DD/MM/YY')
		},
		{
			id:         'status',
			header:     TEXTE.global.status,
			accessorFn: row => TEXTE.order.status[row.status.name],
			cell:       (v) => {
				let status = 'stock';

				if (v.row.original?.status.name === 'refusedByFunder' || v.row.original?.status.name === 'refusedByValala')
					status = 'replacement';
				if (v.row.original?.status.name.startsWith('inPreparation') || v.row.original?.status.name === 'shipped' || v.row.original?.status.name === 'delivered')
					status = '';
				return <span className={`deviceStatus ${status}`}>{TEXTE.order.status[v.row.original?.status.name]}</span>;
			}
		},
		{
			id:     'devices',
			header: TEXTE.global.devices,
			cell:   v => <DevicesBadges addressOrder={v.row.original} />
		},
		{
			id:         'price',
			header:     TEXTE.global.price,
			accessorFn: row => ft_formatPrice(row.price)
		},
		{
			id:     'creator',
			header: TEXTE.order.orderedBy,
			cell:   (v) => {
				const creator = users.find(f => f?._id === v.row.original.creator);

				return (<div><Avatar size={30} user={creator} />{ft_formatUserName(creator)}</div>);
			},
			accessorFn: row => ft_formatUserName(users.find(f => f?._id === row.creator))
		}
	], [users]);

	const table = useReactTable({
		columns,
		data:  useData,
		state: {
			globalFilter: search
		},
		getCoreRowModel:        getCoreRowModel(),
		getFilteredRowModel:    getFilteredRowModel(),
		getSortedRowModel:      getSortedRowModel(),
		getFacetedRowModel:     getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		getFacetedMinMaxValues: getFacetedMinMaxValues()
	});

	useEffect(() => {
		if (!props.limited)
			dispatch(getOrdersApi(user));
	}, []);

	useEffect(() => {
		if (props.limited && !init && params.idOrder && searchParams.get('orderToken')) {
			dispatch(getOrderLimitedApi({
				idOrder:    params.idOrder,
				orderToken: searchParams.get('orderToken') || ''
			}));

			return set_init(true);
		}

		const findOrder = orders.find(find => find?._id === params.idOrder);

		if (!findOrder)
			return set_editData(undefined);

		set_editData(findOrder);
		set_indexAddress(parseInt(params.indexAddress || '0', 10));
	}, [params.idOrder, params.indexAddress, orders, force]);

	function assignDevice(iAddress: number, iDevice: number, iAssign: number, deviceName: string, assignUser?: User) {
		if (!assignUser || !editData)
			return;

		dispatch(putOrderAssignApi({
			user,
			idOrder:      editData._id,
			idUser:       assignUser._id,
			indexDevice:  iDevice.toString(),
			indexAssign:  iAssign.toString(),
			indexAddress: iAddress.toString()
		}))
		.then(() => {
			const notificationData = ft_genNotification(TEXTE, 'assignDeviceSuccess', img_deviceIcon, deviceName);
			ft_pushNotification(dispatch, notificationId, notificationData);
		});
	}

	async function uploadFiles() {
		if (!editData || !documents)
			return;

		const index = documentsUpload.length;
		let error = false;
		set_uploadLoad(true);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const check: any = async (i: number) => {
			const file = Object.entries(documents)[i];

			if (!file) {
				set_uploadLoad(false);

				if (error)
					return;

				set_uploadModal(false);

				if (!props.limited)
					dispatch(getOrdersApi(user));
				else {
					dispatch(getOrderLimitedApi({
						idOrder:    params.idOrder || '',
						orderToken: searchParams.get('orderToken') || ''
					}));
				}
				return;
			}

			if (!file?.[1])
				return check(i + 1);

			const exist = documentsUpload.findIndex(f => f.id === `${editData._id}${file[0]}` && !f.isClose);

			if (exist !== -1) {
				if (typeof documentsUpload[exist].percent === 'number')
					return check(i + 1);

				await dispatch(closeUpload(exist));
			}

			let fileError = null;

			if (file[1].size > 2097152)
				fileError = TEXTE.global.fileToBig;

			const dispatchData = {
				dispatch,
				index:    index + i,
				function: setUploadPercent
			};

			const uploadFileData = {
				percent:  fileError || 0,
				filename: file[1].name,
				size:     file[1].size,
				id:       `${editData._id}${file[0]}`
			};

			await dispatch(pushUpload(uploadFileData));

			if (!fileError) {
				let data = {};

				if (file[0] === 'idCard')
					data = {firstname, lastname};
				if (file[0] === 'idCardSignatory')
					data = {firstname: firstnameSignatory, lastname: lastnameSignatory};
				if (file[0] === 'companyBalance')
					data = {documentDate: moment(documentDate, 'DD/MM/YYYY').toDate()};

				const response = await API.uploadCompanyDocumentOrderToken(file[0], file[1], editData._id, editData.data.token, dispatchData, data);

				if (!response || !response.status) {
					await dispatch(setUploadPercent({index, percent: `error: ${response?.error || ''}`}));
					error = true;
				}

				return check(i + 1);
			}
		};

		check(0);
	}

	function renderOrder(render: React.ReactElement[], order: Order) {
		order?.addressList.forEach((addressOrder, i) => {
			if (!ft_checkOrderFilters(filters, addressOrder))
				return;

			render.push(
				<OrderCard
					order={order}
					indexAddressOrder={i}
					addressOrder={addressOrder}
					onClick={() => {
						if (props.limited && params.idOrder === order._id && params.indexAddress === `${i}`)
							return set_force(force + 1);
						navigate(`/order/${order._id}/${i}${props.limited ? `?orderToken=${order.data.token}` : ''}`);
					}}
					selected={editData?._id === order?._id && indexAddress === i}
				/>
			);
		});
	}

	function renderOrderStatusButton(status: keyof LanguageType['filters']) {
		return (
			<Button
				fullWidth
				size={'sm'}
				onClick={() => set_filters([{key: 'status', values: [status]}])}
				type={filters.some(f => f.key === 'status' && f.values.includes(status)) ? 'secondary' : 'secondary-gray'}
			>
				{TEXTE.filters[status]}
				<Badge size={'sm'} color={'gray'} text={orders.filter(f => f.addressList.some(a => a.status.name === status)).length} />
			</Button>
		);
	}

	function renderOrdersList() {
		let filterCount = 0;

		filters.forEach(filter => filterCount += filter.values.length);

		return (
			<div className={'globalDataContainerItem flex column'} style={{overflow: 'auto'}}>
				<div className={'flex-row'} style={{gap: 12}}>
					<Button
						fullWidth
						size={'sm'}
						onClick={() => set_filters([])}
						type={filters.some(f => f.key === 'status') ? 'secondary-gray' : 'secondary'}
					>
						{TEXTE.global.all}
						<Badge size={'sm'} color={'gray'} text={orders.length} />
					</Button>
					{renderOrderStatusButton('needDocument')}
					{renderOrderStatusButton('contractToBeSigned')}
					{renderOrderStatusButton('shipped')}
				</div>

				<div className={'globalListHeader'}>
					<Button
						size={'sm'}
						type={'tertiary-gray'}
						iconBefore={img_filter}
						disabled={props.limited}
						onClick={() => set_filterModal(true)}
					>
						{TEXTE.global.filter}
						{filterCount > 0 && <Badge size={'sm'} color={'gray'} text={filterCount} />}
					</Button>

					<Input
						fullWidth
						value={search}
						onChange={set_search}
						placeholder={TEXTE.global.search}
						before={<img src={img_search} alt={TEXTE.users.search} />}
					/>
				</div>

				<Table
					data={table}
					load={orderLoad && orders.length === 0}
					onClickRow={({original}) => {
						if (!original || !('i' in original))
							return;

						if (props.limited && params.idOrder === original._id && params.indexAddress === `${original.i}`)
							return set_force(force + 1);
						navigate(`/order/${original._id}/${original.i}${(props.limited && ('token' in original)) ? `?orderToken=${original.token}` : ''}`);
					}}
				/>
			</div>
		);
	}

	function renderOrdersListMobile() {
		const render: React.ReactElement[] = [];
		let filterCount = 0;

		filters.forEach(filter => filterCount += filter.values.length);

		if (orderLoad && orders.length === 0)
			render.push(<Load center />);
		else
			ft_sortOrderByDate(orders, sort as string).forEach(order => renderOrder(render, order));

		return (
			<div className={'globalListContainer'}>
				<div className={'globalListHeader'}>
					<div className={'globalListHeaderButton'}>
						<Button
							fullWidth
							size={'sm'}
							type={'tertiary-gray'}
							iconBefore={img_filter}
							disabled={props.limited}
							onClick={() => set_filterModal(true)}
						>
							{TEXTE.global.filter}
							{filterCount > 0 && <Badge size={'sm'} color={'gray'} text={filterCount} />}
						</Button>

						<div className={'globalListHeaderSeparator'} />

						<Button
							fullWidth
							size={'sm'}
							valueAbsolute={sort}
							type={'tertiary-gray'}
							disabled={props.limited}
							onChangeAbsolute={set_sort}
							absolute={!props.limited ? [
								{value: 'sortRecent', render: TEXTE.global.sortRecent},
								{value: 'sortOlder', render: TEXTE.global.sortOlder}
							] : undefined}
						>
							{TEXTE.global.sortBy}
						</Button>
					</div>
				</div>

				<div className={'globalList'}>
					{render}
				</div>
			</div>
		);
	}

	function renderStepList(fullWidth?: boolean) {
		const stepList = [
			TEXTE.order.selectDevices,
			TEXTE.order.validateFunder,
			TEXTE.order.signContract,
			TEXTE.order.preparation,
			TEXTE.order.devicesdelivery,
			TEXTE.order.deliveryConfirm
		];
		const render: React.ReactElement[] = [];
		let needDocument = false;
		let cancel = false;
		let actual = 0;
		let status = '';

		if (editData && editData.addressList[indexAddress]) {
			status = editData.addressList[indexAddress].status.name;

			if (status === 'underReview' || status === 'beingValidated' || status === 'needDocument')
				actual = 1;
			else if (status === 'contractToBeSigned')
				actual = 2;
			else if (status.startsWith('inPreparation'))
				actual = 3;
			else if (status === 'shipped')
				actual = 4;
			else if (status === 'delivered')
				actual = 6;
			else if (status === 'refusedByFunder' || status === 'refusedByValala' || status === 'cancel')
				cancel = true;

			if (status === 'needDocument' && editData.addressList[indexAddress].data?.missingDoc?.some(f => !f.isSend))
				needDocument = true;
		}

		stepList.forEach((stepText, i) => {
			let color: BadgeColor | undefined;
			let badgeText = '';

			if (i === 0 || actual > i) {
				color = 'success';
				badgeText = TEXTE.order.end;
			} if (i !== 0 && actual === i) {
				color = 'gray';
				badgeText = TEXTE.order.current;
			} if (i === 1 && (cancel || (status === 'needDocument' && needDocument))) {
				color = 'error';
				badgeText = needDocument ? TEXTE.order.status.needDocument : TEXTE.order.status.refusedByFunder;
			}

			render.push(
				<div
					style={i === 1 && needDocument ? {cursor: 'pointer'} : {}}
					onClick={() => i === 1 && needDocument && set_uploadModal(true)}
					className={`orderStepListItem ${color} ${fullWidth && 'fullWidth'}`}
				>
					<div className={'flex flex-column'}>
						{!isComputer &&
							<div className={'flex-center'}>
								<div className={'orderStepListItemNumber'}>{i + 1}</div>
								<div className={'flex text-md weight-500'}>{stepText}</div>
							</div>
						}

						{(color || isComputer) &&
							<div className={'flex'}>
								{isComputer && <div className={'orderStepListItemNumber'}>{i + 1}</div>}
								{isComputer && <div className={'flex text-md weight-500'}>{stepText}</div>}

								{color &&
									<Badge
										size={'sm'}
										color={color}
										text={ft_formatFirstUpperCase(badgeText)}
										style={{
											backgroundColor: (color === 'success' || color === 'error') ? '#ffffff' : ft_cssVar(`${color}-100`)
										}}
									/>
								}
							</div>
						}

						{(i === 1 && status === 'needDocument') &&
							<div className={'flex flex-column'}>
								<br />
								{needDocument ?
									<>
										<span className={'orderStepListItemInfoText'}>{TEXTE.order.needDocument}</span>
										<br />
										<span className={'primary-700 weight-500'}>{TEXTE.order.needDocumentUpload}</span>
									</>
									:
									<span className={'orderStepListItemInfoText'}>{TEXTE.order.checkDocument}</span>
								}
							</div>
						}
					</div>
				</div>
			);
		});

		return render;
	}

	function renderContract(contractFunder?: Funder, idContract?: string) {
		const fileExist = idContract && contractFunder?.file;

		return (
			<div className={`orderDataContractContainer ${!fileExist && 'warning'}`}>
				<Icon
					iconBorder
					icon={img_file}
					type={fileExist ? 'primary' : 'warning'}
					iconColor={ft_cssVar(`${fileExist ? 'primary' : 'warning'}-600`)}
				/>
				<div className={'orderDataContractTextContainer'}>
					<span>{`${TEXTE.global.contract} n°${contractFunder?.file ? contractFunder.file.name : `${editData?.number}.pdf`}`}</span>
					<span>{fileExist ? TEXTE.order.downloadContract : TEXTE.order.yourContractText}</span>
				</div>
				<div>
					<Icon
						icon={img_download}
						iconColor={!fileExist ? ft_cssVar('gray-300') : undefined}
						onClick={fileExist ? () => API.downloadContract(idContract, contractFunder.file, user) : undefined}
					/>
				</div>
			</div>
		);
	}

	function renderContractAddress(addressData: OrderAddressList, i: number) {
		const findAddress = address.find(find => find._id === addressData.address);
		const renderDevices: React.ReactElement[] = [];

		addressData.devices.forEach((device, deviceIndex) => {
			if (!device.shopData)
				return;

			for (let j = 0; j < device.quantity; j++) {
				let deviceData;

				if (device.deviceData?.[j]?.upgrade) {
					renderDevices.push(
						<DeviceCard
							disabled
							hideBadge
							hideAssign
							price={device.price}
							duration={device.duration}
							shopData={device.shopData}
						/>
					);
					renderDevices.push(
						<div className={'flex-center flex-column'} style={{margin: 8, gap: 4}}>
							<span className={'text-sm weight-600 gray-900'}>{TEXTE.global.replace}</span>
							<Icon
								iconBorder
								icon={img_arrowDown}
							/>
						</div>
					);
				}

				if (device.deviceList && device.deviceList[j])
					deviceData = devicesWithLines.find(find => find._id === device.deviceList[j]);

				renderDevices.push(
					<DeviceCard
						showPrice
						hideBadge
						device={deviceData}
						hideAssign={props.limited}
						duration={device.duration}
						price={!deviceData ? device.price : undefined}
						assign={!deviceData ? device.assign[j] : undefined}
						shopData={!deviceData ? device.shopData : undefined}
						onClickAssign={!deviceData ?
							newUser => assignDevice(i, deviceIndex, j, device.shopData.name, newUser)
							:
							undefined
						}
					/>
				);
			}
		});

		return (
			<Section style={{marginBottom: 24}}>
				<span className={'orderDataTitle'}>
					{`${TEXTE.global.delivery} ${i + 1}`}
					<DevicesBadge addressOrder={addressData} />
				</span>

				<div className={'orderDataAddressText text-xs'}>
					<span className={'weight-600'}>{`${TEXTE.global.address} `}</span>
					{findAddress?.name || ''}
					<span className={'weight-600'}>{` ${TEXTE.global.contact} `}</span>
					{`${findAddress?.firstname || ''} ${findAddress?.lastname || ''} ${findAddress?.phone || ''}`}
				</div>

				{renderDevices}
			</Section>
		);
	}

	function renderYourOrder() {
		if (!editData)
			return <></>;

		const contract = contracts.find(find => find._id === editData.contract);
		const renderContracts: React.ReactElement[] = [];
		const renderAddress: React.ReactElement[] = [];
		let total = 0;

		if (contract)
			contract.funderList.forEach(funder => renderContracts.push(renderContract(funder, contract._id)));
		else
			renderContracts.push(renderContract());

		editData.addressList.forEach((addressData, i) => {
			addressData.devices.forEach(device => total += device.price * device.quantity);

			renderAddress.push(renderContractAddress(addressData, i));
		});

		return (
			<div>
				{!props.limited &&
					<Section style={{marginBottom: 24}}>
						<span className={'orderDataTitle'}>{TEXTE.order.yourContract}</span>
						{renderContracts}
					</Section>
				}

				{renderAddress}

				<Section style={{marginBottom: 8}}>
					<div className={'globalDataContainerItem column'}>
						<div className={'orderDataTotalLine'}>
							<span>{TEXTE.global.subTotal}</span>
							<span>{`${ft_formatPrice(total)} HT`}</span>
						</div>
						<div className={'orderDataTotalLine'}>
							<span>{TEXTE.global.delivery}</span>
							<span>{TEXTE.global.free}</span>
						</div>
					</div>
				</Section>

				<Section>
					<div className={'globalDataContainerItem column'}>
						<div className={'orderDataTotalLine'}>
							<span>{TEXTE.global.total}</span>
							<span className={'text-xl weight-600 gray-800'}>
								{ft_formatPrice(total)}<span className={'weight-400 text-regular gray-600'}>{` HT/${TEXTE.global.month}`}</span>
							</span>
						</div>
					</div>
				</Section>
			</div>
		);
	}

	function renderButtonContainer() {
		if (editData && menu === 'yourOrder')
			return;

		const renderButton: React.ReactElement[] = [];

		editData?.addressList[indexAddress]?.trackings?.forEach((tracking) => {
			renderButton.push(
				<Button
					fullWidth
					icon={img_truck}
					targetLink={tracking || undefined}
					onClick={!tracking ? () => navigate('/order/catalog') : undefined}
				>
					{editData ? TEXTE.order.trackDelivery : TEXTE.order.newCommand}
				</Button>
			);
		});

		return (
			<ButtonStickyContainer>
				{renderButton.length > 0 ?
					renderButton
					:
					<Button
						fullWidth
						icon={img_truck}
						disabled={editData && !editData.addressList[indexAddress].tracking}
						targetLink={editData?.addressList[indexAddress]?.tracking || undefined}
						onClick={!editData?.addressList[indexAddress]?.tracking ? () => navigate('/order/catalog') : undefined}
					>
						{editData ? TEXTE.order.trackDelivery : TEXTE.order.newCommand}
					</Button>
				}
			</ButtonStickyContainer>
		);
	}

	function renderOrderData() {
		if (!editData)
			return;

		return (
			<div className={'globalDataContainer'}>
				{!isComputer && <br />}
				{!isComputer &&
					<div className={'header minimize'}>
						<div onClick={() => (props.limited ? set_editData(undefined) : navigate('/order'))}>
							<Icon icon={img_flipBackward} />
							<span className={'text-sm weight-500 gray-600'}>{TEXTE.global.back}</span>
						</div>
					</div>
				}

				<Section sticky>
					<div className={'globalDataContainerItem column'}>
						<div className={'flex flex-center'}>
							<Icon icon={img_boxColored} iconSize={48} />

							<span className={'text-xl weight-600 gray-900'}>
								{`${TEXTE.order.order} n°${editData.number}${(menu === 'trackOrder' && editData.addressList.length > 1) ? `-${indexAddress + 1}` : ''}`}
							</span>

							<Badge
								notRoundImg
								size={'sm'}
								img={img_calendar}
								color={'success'}
								style={{marginLeft: 16}}
								text={moment(editData.data.creationDate).format('DD/MM/YYYY')}
							/>
							<div className={'flex'} />
							{(isComputer && !props.limited) &&
								<Icon
									iconSize={15}
									icon={img_ticket}
									popup={TEXTE.support.openTicket}
									iconColor={ft_cssVar('gray-500')}
									onClick={() => navigate(`/support/new/order/${editData._id}`)}
								/>
							}
						</div>

						<Tabs
							alignLeft
							data={[
								{value: 'trackOrder', render: TEXTE.order.trackOrder},
								{value: 'yourOrder', render: TEXTE.order.yourOrder}
							]}
							actual={menu}
							onChange={set_menu}
						/>
					</div>
				</Section>

				{(menu === 'trackOrder') &&
					<Section>
						<div className={'globalDataContainerItem column'}>
							{renderStepList(true)}
						</div>
					</Section>
				}
				{(menu === 'yourOrder') && renderYourOrder()}

				{renderButtonContainer()}
			</div>
		);
	}

	function renderUploadModal() {
		const render: React.ReactElement[] = [];
		let missing = false;

		editData?.addressList[indexAddress].data?.missingDoc?.forEach((doc) => {
			if (!doc?.name || doc.isSend)
				return;

			const file = documents[doc.name];
			const find = documentsUpload.find(f => !f.isClose && f.id === `${editData._id}${doc.name}`);

			render.push(<span className={'text-sm weight-500'}>{TEXTE.settings.documentsType[doc.name]}</span>);

			if (find) {
				return render.push(
					<DocumentItem
						size={find.size}
						percent={find.percent}
						filename={find.filename}
						onClickDelete={typeof find.percent === 'string' ? () => set_documents({...documents, [doc.name]: undefined}) : undefined}
					/>
				);
			}

			if (file) {
				return render.push(
					<DocumentItem
						size={file.size}
						filename={file.name}
						percent={file.size > 2097152 ? TEXTE.global.fileToBig : ' '}
						onClickDelete={() => set_documents({...documents, [doc.name]: undefined})}
					/>
				);
			}

			missing = true;

			render.push(
				<Input
					type={'file'}
					accept={'.jpg,.jpeg,.tiff,.png,.webp,.bmp,.pdf'}
					fileInputText={TEXTE.devices.deviceDocumentRules}
					onChangeFile={(files) => {
						if (!files?.[0])
							return;

						if (doc.name.startsWith('idCard') || doc.name === 'companyBalance')
							set_modalUpload(doc.name);

						set_documents({...documents, [doc.name]: files[0]});
					}}
				/>
			);
		});

		return (
			<Modal
				gap={12}
				confirmLoad={uploadLoad}
				confirmButton={uploadFiles}
				confirmButtonDisabled={missing}
				onClose={() => set_uploadModal(false)}
				header={TEXTE.order.status.needDocument}
			>
				{render}

				{modalUpload &&
					<Modal
						gap={12}
						confirmLoad={uploadLoad}
						header={modalUpload === 'companyBalance' ? TEXTE.settings.documentDate : TEXTE.settings.idCardData}
						confirmButton={((modalUpload === 'companyBalance' && documentDate) || (modalUpload === 'idCard' && firstname && lastname) || (modalUpload === 'idCardSignatory' && firstnameSignatory && lastnameSignatory)) ? () => set_modalUpload(null) : undefined}
					>
						{modalUpload === 'companyBalance' ?
							<Input
								required
								type={'date'}
								value={documentDate}
								placeholder={'JJ/MM/AAAA'}
								onChange={set_documentDate}
								style={{marginBottom: 300}}
							/>
							:
							<>
								<Input
									required
									title={TEXTE.global.firstname}
									value={modalUpload === 'idCard' ? firstname : firstnameSignatory}
									onChange={modalUpload === 'idCard' ? set_firstname : set_firstnameSignatory}
								/>
								<Input
									required
									title={TEXTE.global.lastname}
									value={modalUpload === 'idCard' ? lastname : lastnameSignatory}
									onChange={modalUpload === 'idCard' ? set_lastname : set_lastnameSignatory}
								/>
							</>
						}
					</Modal>
				}
			</Modal>
		);
	}

	return (
		<div className={'globalMainContainer'}>
			{isComputer ?
				<>
					{!editData ? renderOrdersList() : renderOrderData()}
				</>
				:
				<>
					{editData ? renderOrderData() : renderOrdersListMobile()}
				</>
			}

			{uploadModal && renderUploadModal()}

			{filterModal &&
				<ModalFilter
					isOrder
					filters={filters}
					setFilters={set_filters}
					onClose={() => set_filterModal(false)}
				/>
			}
		</div>
	);
}

export default OrderView;
